import type { TagEntity } from '@experiences/interfaces';
import urljoin from 'url-join';

import {
    get,
    post,
} from '../utility/Requests';

type RCSEntity = {
    entityKey: string;
    name: string;
    description: string;
    entityType: string;
    folders: RSCFolder[];
    folderKey: string | null;
    folderKeys: string[];
    customData: string;
};

type RSCFolder = {
    key: string;
    displayName: string;
    fullyQualifiedName: string;
};

export type ProcessDefinition = {
    name: string;
    type: string;
    folderKey?: string;
    folderName?: string;
    compositeKey?: string;
    resourceKey?: string;
    inputs: Record<string, string>;
    outputs: Record<string, string>;
    customData: string;
    metadata: {
        description: string;
        key: string;
        name: string;
        entityKey: string;
        folderKey: string;
        folderName: string;
        folderPath?: string;
    };
};

// Supported Resource Catalog process subtypes:
// - 'processorchestration': Maestro BPMN
// - 'process': RPA workflow (Orchestrator process)
// - 'api': API workflow
// - 'agent': Agents
// - 'caseManagement': Maestro Case
// - 'flow': Maestro Flow
export type EntitySubType = 'agent' | 'process' | 'api' | 'processorchestration' | 'caseManagement' | 'flow';

function mapToProcessDefinitionsRecord(
    entities: RCSEntity[],
    entitySubType: string,
): Record<string, ProcessDefinition> {
    return entities.reduce<Record<string, ProcessDefinition>>((acc, entity) => {
        const key = entity.entityKey;
        if (!key) {
            return acc;
        }

        const folderKey = entity.folderKey ?? undefined;
        const matchingFolder = folderKey
            ? entity.folders.find(folder => folder.key === folderKey)
            : entity.folders[0];
        const folderName = matchingFolder?.displayName;
        const folderPath = matchingFolder?.fullyQualifiedName;

        acc[key] = {
            name: entity.name,
            type: entitySubType || entity.entityType,
            folderKey,
            folderName,
            resourceKey: entity.entityKey,
            inputs: {},
            outputs: {},
            customData: entity.customData,
            metadata: {
                description: entity.description,
                key,
                name: entity.name,
                entityKey: entity.entityKey,
                folderKey: folderKey ?? '',
                folderName: folderName ?? '',
                folderPath,
            },
        };

        return acc;
    }, {});
}

const resourceCatalogBasePath = '/resourcecatalog_';

export async function getAllResourceCatalogProcesses({
    tenantName,
    entitySubType,
    searchText,
}: {
    tenantName: string;
    entitySubType?: string;
    searchText?: string;
}): Promise<Record<string, ProcessDefinition>> {
    const allProcesses: RCSEntity[] = [];

    const take = 100;
    const maxIterations = 1000; // safety guard to prevent infinite loops
    let skip = 0;
    let hasMoreData = true;
    let iterations = 0;

    const baseUrl = urljoin('/', tenantName, resourceCatalogBasePath, '/Entities/Processes');
    while (hasMoreData && iterations < maxIterations) {
        const response = await get<{ value: RCSEntity[] }>(baseUrl, {
            stripPortalPath: true,
            urlParams: {
                ...(entitySubType ? { entitySubType } : {}),
                take,
                skip,
                ...(searchText ? { name: searchText } : {}),
            },
        });
        const processes = response.value ?? [];
        allProcesses.push(...processes);

        // If we received fewer than the requested amount, we've reached the end.
        hasMoreData = processes.length === take;
        skip += take;
        iterations++;
    }

    if (iterations >= maxIterations) {
        // eslint-disable-next-line no-console
        console.warn(`getAllResourceCatalogProcesses reached maximum iterations (${maxIterations}). Some data may be missing.`);
    }
    return mapToProcessDefinitionsRecord(allProcesses, entitySubType ?? '');
}

export async function getResourceCatalogProcessesByKeys({
    tenantName,
    entityKeys,
}: {
    tenantName: string;
    entityKeys: string[];
}): Promise<Record<string, ProcessDefinition>> {
    if (!entityKeys.length) {
        return {};
    }

    const url = urljoin('/', tenantName, resourceCatalogBasePath, '/Entities/GetEntitiesByKeys');

    const entities = await post<RCSEntity[]>(url, {
        stripPortalPath: true,
        body: entityKeys,
    });

    return mapToProcessDefinitionsRecord(entities ?? [], '');
}

export async function getAllResourceCatalogTags({
    tenantName,
    searchText,
}: {
    tenantName: string;
    searchText?: string;
}): Promise<TagEntity[]> {
    const url = urljoin('/', tenantName, resourceCatalogBasePath, '/Tags');
    const response = await get<{ value: TagEntity[] }>(url, {
        stripPortalPath: true,
        urlParams: {
            skip: 0,
            take: 100,
            startsWith: searchText ?? undefined,
            type: 'Label',
        },
    });
    return response.value ?? [];
}
